<template>
  <div class="shop-order">
    <el-card>
      <div class="menu-box">
        <h2>便利店订单管理</h2>
        <div class="box-bd">
          <!-- 搜索栏 -->
          <el-row type="flex" :gutter="12" class="search-bar">
            <el-col :span="5">
              <el-input
                size="small"
                clearable
                placeholder="订单编号"
                prefix-icon="el-icon-document"
                v-model="query.order_no"
                @keyup.enter.native="handleSearch"
              />
            </el-col>
            <el-col :span="5">
              <el-input
                size="small"
                clearable
                placeholder="收货人姓名"
                prefix-icon="el-icon-user"
                v-model="query.consignee_name"
                @keyup.enter.native="handleSearch"
              />
            </el-col>
            <el-col :span="5">
              <el-input
                size="small"
                clearable
                placeholder="联系电话"
                prefix-icon="el-icon-phone"
                v-model="query.consignee_phone"
                @keyup.enter.native="handleSearch"
              />
            </el-col>
            <el-col :span="5">
              <el-date-picker
                size="small"
                v-model="query.date_range"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="yyyy-MM-dd"
                style="width:100%"
              />
            </el-col>
            <el-col :span="4">
              <el-button type="primary" icon="el-icon-search" size="small" @click="handleSearch">查 询</el-button>
              <el-button icon="el-icon-refresh" size="small" @click="resetQuery">重 置</el-button>
            </el-col>
          </el-row>

          <!-- 状态Tab -->
          <el-row class="status-tabs">
            <el-radio-group v-model="query.status" size="small" @change="handleStatusChange">
              <el-radio-button label="">全部</el-radio-button>
              <el-radio-button label="0">待付款</el-radio-button>
              <el-radio-button label="1">待发货</el-radio-button>
              <el-radio-button label="2">待收货</el-radio-button>
              <el-radio-button label="3">已完成</el-radio-button>
              <el-radio-button label="4">已取消</el-radio-button>
            </el-radio-group>
          </el-row>

          <!-- 统计卡片 -->
          <el-row :gutter="16" class="stat-row">
            <el-col :span="4">
              <div class="stat-card all">
                <div class="stat-num">{{ stats.total }}</div>
                <div class="stat-label">全部订单</div>
              </div>
            </el-col>
            <el-col :span="4">
              <div class="stat-card pending-pay">
                <div class="stat-num">{{ stats.pendingPay }}</div>
                <div class="stat-label">待付款</div>
              </div>
            </el-col>
            <el-col :span="4">
              <div class="stat-card pending-ship">
                <div class="stat-num">{{ stats.pendingShip }}</div>
                <div class="stat-label">待发货</div>
              </div>
            </el-col>
            <el-col :span="4">
              <div class="stat-card pending-receive">
                <div class="stat-num">{{ stats.pendingReceive }}</div>
                <div class="stat-label">待收货</div>
              </div>
            </el-col>
            <el-col :span="4">
              <div class="stat-card completed">
                <div class="stat-num">{{ stats.completed }}</div>
                <div class="stat-label">已完成</div>
              </div>
            </el-col>
            <el-col :span="4">
              <div class="stat-card cancelled">
                <div class="stat-num">{{ stats.cancelled }}</div>
                <div class="stat-label">已取消</div>
              </div>
            </el-col>
          </el-row>

          <!-- 订单列表 -->
          <div class="order-list" v-loading="tableLoading">
            <div v-if="tableData.length === 0" class="empty-box">
              <i class="el-icon-folder-opened"></i>
              <p>暂无订单数据</p>
            </div>
            <div v-else>
              <div v-for="order in tableData" :key="order.id" class="order-item">
                <!-- 订单头部 -->
                <div class="order-header">
                  <div class="header-left">
                    <span class="order-time">{{ order.create_time }}</span>
                    <span class="order-no">订单号：{{ order.order_no }}</span>
                  </div>
                  <div class="header-right">
                    <el-tag size="small" :type="getStatusTagType(order.status)">
                      {{ getStatusText(order.status) }}
                    </el-tag>
                  </div>
                </div>

                <!-- 商品信息 -->
                <div class="order-goods" @click="openDetail(order)">
                  <div class="goods-list">
                    <div v-for="(goods, index) in order.goods_list" :key="index" class="goods-item">
                      <el-image
                        v-if="goods.goods_image"
                        :src="goods.goods_image"
                        fit="cover"
                        class="goods-image"
                      >
                        <div slot="error" class="image-slot">
                          <i class="el-icon-picture-outline"></i>
                        </div>
                      </el-image>
                      <div v-else class="goods-image goods-no-image">
                        <i class="el-icon-goods"></i>
                      </div>
                      <div class="goods-info">
                        <div class="goods-name">{{ goods.goods_name }}</div>
                        <div class="goods-spec" v-if="goods.spec_name">{{ goods.spec_name }}</div>
                        <div class="goods-price">
                          <span class="price">¥{{ goods.price }}</span>
                          <span class="num">x{{ goods.num }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="order-summary">
                    <div class="summary-row">
                      <span class="label">共 {{ order.total_num }} 件商品</span>
                      <span class="amount">
                        合计：<span class="price-text">¥{{ order.total_amount }}</span>
                        <span v-if="order.freight > 0" class="freight">（含运费¥{{ order.freight }}）</span>
                      </span>
                    </div>
                  </div>
                </div>

                <!-- 收货人信息 -->
                <div class="order-receiver">
                  <i class="el-icon-location"></i>
                  <span>{{ order.consignee_name }} {{ order.consignee_phone }}</span>
                  <span class="address">{{ order.address }}</span>
                </div>

                <!-- 订单操作 -->
                <div class="order-actions">
                  <div class="action-left">
                    <el-button
                      v-if="order.remark"
                      size="mini"
                      type="info"
                      plain
                      @click="viewRemark(order)"
                    >
                      查看备注
                    </el-button>
                  </div>
                  <div class="action-right">
                    <el-button
                      v-if="order.status === 0"
                      size="mini"
                      type="danger"
                      @click="cancelOrderHandle(order)"
                    >取消订单</el-button>
                    <el-button
                      v-if="order.status === 1"
                      size="mini"
                      type="primary"
                      @click="deliverOrder(order)"
                    >立即发货</el-button>
                    <el-button
                      v-if="order.status === 2"
                      size="mini"
                      type="success"
                      @click="confirmReceiveHandle(order)"
                    >确认收货</el-button>
                    <el-button
                      v-if="[3, 4].includes(order.status)"
                      size="mini"
                      @click="deleteOrderHandle(order)"
                    >删除订单</el-button>
                    <el-button
                      size="mini"
                      icon="el-icon-search"
                      @click="openDetail(order)"
                    >详情</el-button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 分页 -->
          <el-pagination
            class="pagination"
            :pager-count="7"
            @current-change="pageChange"
            @size-change="sizeChange"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :page-size="query.page_num"
            background
            :total="exp.total"
          />
        </div>
      </div>
    </el-card>

    <!-- 订单详情弹窗 -->
    <el-dialog
      v-dialogDrag
      title="订单详情"
      :visible.sync="detailVisible"
      width="800px"
    >
      <div v-if="detailInfo" class="detail-box">
        <!-- 基本信息 -->
        <div class="detail-section">
          <div class="section-title">基本信息</div>
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="订单编号">{{ detailInfo.order_no }}</el-descriptions-item>
            <el-descriptions-item label="订单状态">
              <el-tag size="small" :type="getStatusTagType(detailInfo.status)">
                {{ getStatusText(detailInfo.status) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="下单时间">{{ detailInfo.create_time }}</el-descriptions-item>
            <el-descriptions-item label="支付时间">{{ detailInfo.pay_time || '-' }}</el-descriptions-item>
            <el-descriptions-item label="发货时间">{{ detailInfo.ship_time || '-' }}</el-descriptions-item>
            <el-descriptions-item label="完成时间">{{ detailInfo.finish_time || '-' }}</el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 收货人信息 -->
        <div class="detail-section">
          <div class="section-title">收货人信息</div>
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="收货人">{{ detailInfo.consignee_name }}</el-descriptions-item>
            <el-descriptions-item label="联系电话">{{ detailInfo.consignee_phone }}</el-descriptions-item>
            <el-descriptions-item label="收货地址" :span="2">{{ detailInfo.address }}</el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 商品信息 -->
        <div class="detail-section">
          <div class="section-title">商品信息</div>
          <el-table :data="detailInfo.goods_list" border size="small">
            <el-table-column label="商品图片" width="80">
              <template v-slot="scope">
                <el-image
                  v-if="scope.row.goods_image"
                  :src="scope.row.goods_image"
                  fit="cover"
                  style="width: 50px; height: 50px;"
                >
                  <div slot="error" class="image-slot">
                    <i class="el-icon-picture-outline"></i>
                  </div>
                </el-image>
                <i v-else class="el-icon-goods" style="font-size: 24px; color: #999;"></i>
              </template>
            </el-table-column>
            <el-table-column prop="goods_name" label="商品名称" min-width="150" show-overflow-tooltip />
            <el-table-column prop="spec_name" label="规格" width="120" />
            <el-table-column prop="price" label="单价" width="100">
              <template v-slot="scope">¥{{ scope.row.price }}</template>
            </el-table-column>
            <el-table-column prop="num" label="数量" width="80" align="center" />
            <el-table-column label="小计" width="100">
              <template v-slot="scope">¥{{ (scope.row.price * scope.row.num).toFixed(2) }}</template>
            </el-table-column>
          </el-table>
          <div class="amount-summary">
            <span>商品总价：<i>¥{{ detailInfo.goods_amount || detailInfo.total_amount }}</i></span>
            <span v-if="detailInfo.freight > 0">运费：<i>¥{{ detailInfo.freight }}</i></span>
            <span class="total">订单总价：<i>¥{{ detailInfo.total_amount }}</i></span>
          </div>
        </div>

        <!-- 支付信息 -->
        <div class="detail-section" v-if="detailInfo.pay_type">
          <div class="section-title">支付信息</div>
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="支付方式">
              {{ getPayTypeText(detailInfo.pay_type) }}
            </el-descriptions-item>
            <el-descriptions-item label="支付状态">
              <el-tag v-if="detailInfo.pay_status === 1" size="small" type="success">已支付</el-tag>
              <el-tag v-else size="small" type="info">未支付</el-tag>
            </el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 备注信息 -->
        <div class="detail-section" v-if="detailInfo.remark">
          <div class="section-title">备注信息</div>
          <el-input type="textarea" v-model="detailInfo.remark" :rows="2" readonly />
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <!-- 发货弹窗 -->
    <el-dialog
      v-dialogDrag
      title="订单发货"
      :visible.sync="deliverVisible"
      width="500px"
    >
      <el-form :model="deliverForm" label-width="100px">
        <el-form-item label="快递公司">
          <el-select v-model="deliverForm.express_company" placeholder="请选择快递公司" style="width: 100%;">
            <el-option label="顺丰速运" value="顺丰速运" />
            <el-option label="中通快递" value="中通快递" />
            <el-option label="圆通速递" value="圆通速递" />
            <el-option label="韵达快递" value="韵达快递" />
            <el-option label="申通快递" value="申通快递" />
            <el-option label="极兔速递" value="极兔速递" />
            <el-option label="京东物流" value="京东物流" />
            <el-option label=" EMS" value="EMS" />
            <el-option label="其他" value="其他" />
          </el-select>
        </el-form-item>
        <el-form-item label="快递单号">
          <el-input v-model="deliverForm.express_no" placeholder="请输入快递单号" />
        </el-form-item>
        <el-form-item label="发货备注">
          <el-input v-model="deliverForm.remark" type="textarea" :rows="3" placeholder="选填" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="deliverVisible = false">取 消</el-button>
        <el-button type="primary" :loading="btnLoading" @click="confirmDeliver">确认发货</el-button>
      </div>
    </el-dialog>

    <!-- 备注查看弹窗 -->
    <el-dialog
      v-dialogDrag
      title="订单备注"
      :visible.sync="remarkVisible"
      width="400px"
    >
      <div class="remark-box">
        <p>{{ currentOrder.remark || '暂无备注' }}</p>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="remarkVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getOrderList,
  getOrderDetail,
  orderDeliver,
  cancelOrder,
  deleteOrder,
  confirmReceive,
} from '@/api/zhiqing_shop/order.js'

export default {
  name: 'ShopOrderList',
  data() {
    return {
      tableLoading: false,
      exp: {
        total: 0,
      },
      btnLoading: false,
      query: {
        page_code: 1,
        page_num: 10,
        order_no: '',
        consignee_name: '',
        consignee_phone: '',
        status: '',
        date_range: [],
        start_date: '',
        end_date: '',
      },
      stats: {
        total: 0,
        pendingPay: 0,
        pendingShip: 0,
        pendingReceive: 0,
        completed: 0,
        cancelled: 0,
      },
      tableData: [],
      detailVisible: false,
      detailInfo: null,
      deliverVisible: false,
      deliverForm: {
        order_id: '',
        express_company: '',
        express_no: '',
        remark: '',
      },
      remarkVisible: false,
      currentOrder: {},
    }
  },
  created() {
    this.getList()
  },
  methods: {
    // 搜索
    handleSearch() {
      this.query.page_code = 1
      this.getList()
    },
    // 重置搜索
    resetQuery() {
      this.query = {
        page_code: 1,
        page_num: 10,
        order_no: '',
        consignee_name: '',
        consignee_phone: '',
        status: '',
        date_range: [],
        start_date: '',
        end_date: '',
      }
      this.getList()
    },
    // 状态Tab切换
    handleStatusChange(val) {
      this.query.status = val
      this.query.page_code = 1
      this.getList()
    },
    // 分页
    pageChange(val) {
      this.query.page_code = val
      this.getList()
    },
    sizeChange(val) {
      this.query.page_num = val
      this.query.page_code = 1
      this.getList()
    },
    // 获取订单列表
    async getList() {
      this.tableLoading = true
      // 处理日期范围
      if (this.query.date_range && this.query.date_range.length === 2) {
        this.query.start_date = this.query.date_range[0]
        this.query.end_date = this.query.date_range[1]
      } else {
        this.query.start_date = ''
        this.query.end_date = ''
      }
      
      const params = { ...this.query }
      delete params.date_range
      
      try {
        const { data: res } = await getOrderList(params)
        if (res.code === 1) {
          // 处理订单数据，转换字段名称以匹配页面使用的字段
          this.tableData = (res.data.list || []).map(order => ({
            ...order,
            create_time: order.create_time_str, // 转换创建时间字段
            total_num: order.goods_list.reduce((sum, item) => sum + item.quantity, 0), // 计算总商品数
            freight: order.shipping_fee, // 转换运费字段
            consignee_name: order.nickname, // 使用nickname作为收货人姓名
            consignee_phone: order.mobile, // 使用mobile作为联系电话
            address: '', // 接口中没有地址信息，暂时为空
            // 转换商品列表中的字段
            goods_list: order.goods_list.map(goods => ({
              ...goods,
              num: goods.quantity // 转换数量字段
            }))
          }))
          this.exp.total = res.data.total || 0
          this.calcStats()
        } else {
          this.$message.error(res.message || '获取数据失败')
        }
      } catch (error) {
        this.$message.error('网络错误，请稍后重试')
      }
      this.tableLoading = false
    },
    // 计算统计数据
    calcStats() {
      this.stats.total = this.exp.total
      // 需要从后端获取准确的统计数据，这里暂时按当前页计算
      this.stats.pendingPay = this.tableData.filter(item => item.status === 0).length
      this.stats.pendingShip = this.tableData.filter(item => item.status === 1).length
      this.stats.pendingReceive = this.tableData.filter(item => item.status === 2).length
      this.stats.completed = this.tableData.filter(item => item.status === 3).length
      this.stats.cancelled = this.tableData.filter(item => item.status === 4).length
    },
    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        0: '待付款',
        1: '待发货',
        2: '待收货',
        3: '已完成',
        4: '已取消',
      }
      return statusMap[status] || '未知'
    },
    // 获取状态标签类型
    getStatusTagType(status) {
      const typeMap = {
        0: 'warning',
        1: 'primary',
        2: 'info',
        3: 'success',
        4: 'info',
      }
      return typeMap[status] || ''
    },
    // 获取支付方式文本
    getPayTypeText(type) {
      const typeMap = {
        1: '微信支付',
        2: '支付宝',
        3: '余额支付',
        4: '银行卡',
      }
      return typeMap[type] || '其他'
    },
    // 打开详情
    async openDetail(row) {
      try {
        const { data: res } = await getOrderDetail({ id: row.id })
        if (res.code === 1) {
          // 处理详情数据，转换字段名称以匹配页面使用的字段
          this.detailInfo = {
            ...res.data,
            create_time: res.data.create_time_str, // 转换创建时间字段
            freight: res.data.shipping_fee, // 转换运费字段
            consignee_name: res.data.nickname, // 使用nickname作为收货人姓名
            consignee_phone: res.data.mobile, // 使用mobile作为联系电话
            address: '', // 接口中没有地址信息，暂时为空
            // 转换商品列表中的字段
            goods_list: (res.data.goods_list || []).map(goods => ({
              ...goods,
              num: goods.quantity // 转换数量字段
            }))
          }
          this.detailVisible = true
        } else {
          this.$message.error(res.message || '获取详情失败')
        }
      } catch (error) {
        this.$message.error('网络错误，请稍后重试')
      }
    },
    // 查看备注
    viewRemark(order) {
      this.currentOrder = order
      this.remarkVisible = true
    },
    // 取消订单
    cancelOrderHandle(order) {
      this.$confirm(`确认取消订单「${order.order_no}」？`, '取消订单', {
        type: 'warning',
        confirmButtonText: '确认取消',
        cancelButtonText: '返回',
      })
        .then(async () => {
          try {
            const { data: res } = await cancelOrder({ id: order.id })
            if (res.code === 200) {
              this.$message.success('订单已取消')
              this.getList()
            } else {
              this.$message.error(res.message || '取消失败')
            }
          } catch (error) {
            this.$message.error('网络错误，请稍后重试')
          }
        })
        .catch(() => {})
    },
    // 删除订单
    deleteOrderHandle(order) {
      this.$confirm(`确认删除订单「${order.order_no}」？删除后不可恢复！`, '删除订单', {
        type: 'warning',
        confirmButtonText: '确认删除',
        cancelButtonText: '返回',
      })
        .then(async () => {
          try {
            const { data: res } = await deleteOrder({ id: order.id })
            if (res.code === 200) {
              this.$message.success('订单已删除')
              this.getList()
            } else {
              this.$message.error(res.message || '删除失败')
            }
          } catch (error) {
            this.$message.error('网络错误，请稍后重试')
          }
        })
        .catch(() => {})
    },
    // 立即发货
    deliverOrder(order) {
      this.deliverForm = {
        order_id: order.id,
        express_company: '',
        express_no: '',
        remark: '',
      }
      this.deliverVisible = true
    },
    // 确认发货
    async confirmDeliver() {
      if (!this.deliverForm.express_company) {
        this.$message.warning('请选择快递公司')
        return
      }
      if (!this.deliverForm.express_no) {
        this.$message.warning('请输入快递单号')
        return
      }
      this.btnLoading = true
      try {
        const { data: res } = await orderDeliver(this.deliverForm)
        if (res.code === 200) {
          this.$message.success('发货成功')
          this.deliverVisible = false
          this.getList()
        } else {
          this.$message.error(res.message || '发货失败')
        }
      } catch (error) {
        this.$message.error('网络错误，请稍后重试')
      }
      this.btnLoading = false
    },
    // 确认收货
    confirmReceiveHandle(order) {
      this.$confirm(`确认已收到货物？`, '确认收货', {
        type: 'success',
        confirmButtonText: '确认收货',
        cancelButtonText: '返回',
      })
        .then(async () => {
          try {
            const { data: res } = await confirmReceive({ id: order.id })
            if (res.code === 200) {
              this.$message.success('已确认收货')
              this.getList()
            } else {
              this.$message.error(res.message || '操作失败')
            }
          } catch (error) {
            this.$message.error('网络错误，请稍后重试')
          }
        })
        .catch(() => {})
    },
  },
}
</script>

<style scoped>
.shop-order {
  padding: 0;
}

.menu-box h2 {
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 600;
}

.box-bd {
  margin-top: 20px;
}

.search-bar {
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.status-tabs {
  margin-bottom: 15px;
}

.stat-row {
  margin-bottom: 20px;
}

.stat-card {
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-card.all { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.stat-card.pending-pay { background: linear-gradient(135deg, #f7971e 0%, #ffd200 100%); }
.stat-card.pending-ship { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
.stat-card.pending-receive { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%); }
.stat-card.completed { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); }
.stat-card.cancelled { background: linear-gradient(135deg, #a8a8a8 0%, #666666 100%); }

.stat-num {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}

.stat-label {
  font-size: 13px;
  margin-top: 4px;
  opacity: 0.9;
}

.order-list {
  min-height: 200px;
}

.empty-box {
  text-align: center;
  padding: 60px 0;
  color: #999;
}

.empty-box i {
  font-size: 48px;
  margin-bottom: 10px;
}

.order-item {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  margin-bottom: 15px;
  overflow: hidden;
  background: #fff;
  transition: box-shadow 0.3s;
}

.order-item:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.order-time {
  color: #909399;
  font-size: 13px;
}

.order-no {
  color: #606266;
  font-size: 13px;
}

.order-goods {
  padding: 15px;
  cursor: pointer;
}

.goods-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.goods-item {
  display: flex;
  align-items: center;
  width: calc(33.33% - 10px);
  min-width: 280px;
  padding: 10px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: #fafafa;
}

.goods-image {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  flex-shrink: 0;
}

.goods-no-image {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  color: #c0c4cc;
  font-size: 24px;
}

.image-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f5f5f5;
  color: #c0c4cc;
  font-size: 20px;
}

.goods-info {
  flex: 1;
  margin-left: 10px;
  overflow: hidden;
}

.goods-name {
  font-size: 14px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.goods-spec {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.goods-price {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 13px;
}

.goods-price .price {
  color: #f56c6c;
  font-weight: 600;
}

.goods-price .num {
  color: #909399;
}

.order-summary {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #ebeef5;
  text-align: right;
}

.summary-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
}

.summary-row .label {
  color: #909399;
  font-size: 13px;
}

.summary-row .amount {
  color: #606266;
  font-size: 14px;
}

.price-text {
  color: #f56c6c;
  font-weight: 600;
  font-size: 16px;
}

.freight {
  color: #909399;
  font-size: 12px;
}

.order-receiver {
  padding: 10px 15px;
  background: #f5f7fa;
  border-top: 1px solid #ebeef5;
  font-size: 13px;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-receiver i {
  color: #409eff;
}

.order-receiver .address {
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-top: 1px solid #ebeef5;
}

.action-right {
  display: flex;
  gap: 8px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

/* 详情弹窗样式 */
.detail-box {
  padding: 0 4px;
}

.detail-section {
  margin-bottom: 20px;
}

.section-title {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  border-left: 3px solid #409eff;
  padding-left: 8px;
  margin-bottom: 10px;
}

.amount-summary {
  display: flex;
  justify-content: flex-end;
  gap: 20px;
  padding: 15px;
  background: #f5f7fa;
  border-radius: 4px;
  margin-top: 10px;
}

.amount-summary span {
  color: #606266;
  font-size: 14px;
}

.amount-summary span i {
  font-style: normal;
  color: #f56c6c;
  font-weight: 600;
}

.amount-summary .total i {
  font-size: 16px;
}

/* 备注弹窗 */
.remark-box {
  padding: 20px;
  background: #f5f7fa;
  border-radius: 4px;
  min-height: 80px;
}

.remark-box p {
  margin: 0;
  color: #606266;
  line-height: 1.6;
  white-space: pre-wrap;
}
</style>
